import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { ChevronRight, ExpandMore, FolderOpen } from '@mui/icons-material';
import { TreeItem, TreeView } from '@mui/x-tree-view';

import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import {
  ROOT_NODE_ID,
  getChildDirectories,
  getDirectoryIdFromNodeId,
  getDirectoryNodeId,
} from './documentDirectoryUtils';
import {
  getDirectoryType,
  getDirectoryTypeInfo,
  isSystemDirectory,
} from './documentDirectoryTypes';
import {
  ISystemDirectorySource,
  getSystemDirectoryDocuments,
} from './systemDirectoryContent';

interface IProps {
  documentDirectoryArray: IDocumentDirectory[];
  documentArray: IDocumentDirectoryHasDocument[];
  systemSource: ISystemDirectorySource;
  selectedDirectoryId: number | null;
  setSelectedDirectoryId: (idDocumentDirectory: number | null) => void;
  expandedNodeIds: string[];
  setExpandedNodeIds: (nodeIdArray: string[]) => void;
  /** ID des Ordners, ueber dem gerade gezogen wird (Drag & Drop-Hervorhebung). */
  dropTargetId: number | null | undefined;
  onDragOverDirectory: (idDocumentDirectory: number | null) => void;
  onDragLeaveDirectory: () => void;
  onDropOnDirectory: (idDocumentDirectory: number | null) => void;
  onDragStartDirectory: (idDocumentDirectory: number) => void;
  /** Ordner vor/hinter einen anderen einsortieren (Drop am Rand einer Zeile). */
  onReorderDirectory: (
    idTargetDocumentDirectory: number,
    placeAfter: boolean
  ) => void;
  isReadOnly: boolean;
}

/**
 * Wo innerhalb der Zeile losgelassen wurde: an den Raendern wird umsortiert, in
 * der Mitte in den Ordner hinein verschoben.
 */
type IDropZone = 'before' | 'inside' | 'after';

const getDropZone = (event: React.DragEvent): IDropZone => {
  const bounds = event.currentTarget.getBoundingClientRect();
  const offset = event.clientY - bounds.top;

  if (offset < bounds.height * 0.28) {
    return 'before';
  }

  if (offset > bounds.height * 0.72) {
    return 'after';
  }

  return 'inside';
};

/**
 * Linke Haelfte des Dateibrowsers: die Ordnerstruktur als Baumansicht.
 * Dateien werden hier bewusst nicht angezeigt — sie stehen in der rechten
 * Haelfte im Inhalt des ausgewaehlten Ordners.
 */
export const DocumentDirectoryTree: React.FC<IProps> = props => {
  const theme = useTheme();

  const handleNodeSelect = (_event: React.SyntheticEvent, nodeId: string) => {
    props.setSelectedDirectoryId(getDirectoryIdFromNodeId(nodeId));
  };

  const [dropZone, setDropZone] = React.useState<IDropZone>('inside');

  /** Wurzelknoten: nur „hinein" moeglich. */
  const buildRootDropHandlers = () => ({
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDropZone('inside');
      props.onDragOverDirectory(null);
    },
    onDragLeave: (event: React.DragEvent) => {
      event.stopPropagation();
      props.onDragLeaveDirectory();
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      props.onDropOnDirectory(null);
    },
  });

  const buildDropHandlers = (idDocumentDirectory: number) => ({
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDropZone(getDropZone(event));
      props.onDragOverDirectory(idDocumentDirectory);
    },
    onDragLeave: (event: React.DragEvent) => {
      event.stopPropagation();
      props.onDragLeaveDirectory();
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const zone = getDropZone(event);

      if (zone === 'inside') {
        props.onDropOnDirectory(idDocumentDirectory);
      } else {
        props.onReorderDirectory(idDocumentDirectory, zone === 'after');
      }
    },
  });

  const getDropSx = (idDocumentDirectory: number | null) => {
    const isTarget = props.dropTargetId === idDocumentDirectory;
    const markerColor = theme.palette.primary.main;

    return {
      border: '2px dashed',
      borderRadius: 1,
      borderColor:
        isTarget && dropZone === 'inside' ? 'primary.main' : 'transparent',
      boxShadow:
        isTarget && dropZone === 'before'
          ? `inset 0 2px 0 0 ${markerColor}`
          : isTarget && dropZone === 'after'
            ? `inset 0 -2px 0 0 ${markerColor}`
            : 'none',
    };
  };

  /** Anzahl der Dokumente direkt im Ordner — bei Systemordnern die Spiegelung. */
  const getDocumentCount = (documentDirectory: IDocumentDirectory): number => {
    const directoryType = getDirectoryType(documentDirectory);

    if (directoryType !== 'standard') {
      return getSystemDirectoryDocuments(directoryType, props.systemSource)
        .length;
    }

    return props.documentArray.filter(
      documentObject =>
        Number(documentObject.idDocumentDirectory) ===
        documentDirectory.idDocumentDirectory
    ).length;
  };

  const renderDirectory = (documentDirectory: IDocumentDirectory) => {
    const directoryType = getDirectoryType(documentDirectory);
    const Icon = getDirectoryTypeInfo(directoryType).icon;
    const documentCount = getDocumentCount(documentDirectory);
    const childArray = getChildDirectories(
      props.documentDirectoryArray,
      documentDirectory.idDocumentDirectory
    );

    return (
      <TreeItem
        key={getDirectoryNodeId(documentDirectory.idDocumentDirectory)}
        nodeId={getDirectoryNodeId(documentDirectory.idDocumentDirectory)}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.25,
              ...getDropSx(documentDirectory.idDocumentDirectory),
            }}
            draggable={!props.isReadOnly}
            onDragStart={(event: React.DragEvent) => {
              event.stopPropagation();
              props.onDragStartDirectory(documentDirectory.idDocumentDirectory);
            }}
            {...buildDropHandlers(documentDirectory.idDocumentDirectory)}
          >
            <Icon
              fontSize='small'
              color={
                isSystemDirectory(documentDirectory) ? 'primary' : 'action'
              }
            />
            <Typography
              variant='body2'
              noWrap
              title={documentDirectory.Title}
              sx={{ flexGrow: 1, minWidth: 0 }}
            >
              {documentDirectory.Title}
            </Typography>
            {documentCount > 0 && (
              <Chip
                size='small'
                label={documentCount}
                title={`${documentCount} Dokument(e) in diesem Ordner`}
                sx={{
                  height: 18,
                  '& .MuiChip-label': { px: 0.75, fontSize: 11 },
                }}
              />
            )}
          </Box>
        }
      >
        {childArray.map(child => renderDirectory(child))}
      </TreeItem>
    );
  };

  return (
    <TreeView
      aria-label='Ordnerstruktur'
      defaultCollapseIcon={<ExpandMore />}
      defaultExpandIcon={<ChevronRight />}
      expanded={props.expandedNodeIds}
      selected={
        props.selectedDirectoryId === null
          ? ROOT_NODE_ID
          : getDirectoryNodeId(props.selectedDirectoryId)
      }
      onNodeToggle={(_event: React.SyntheticEvent, nodeIdArray: string[]) =>
        props.setExpandedNodeIds(nodeIdArray)
      }
      onNodeSelect={handleNodeSelect}
      sx={{ overflowX: 'hidden' }}
    >
      <TreeItem
        nodeId={ROOT_NODE_ID}
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.25,
              ...getDropSx(null),
            }}
            {...buildRootDropHandlers()}
          >
            <FolderOpen fontSize='small' color='action' />
            <Typography variant='body2' fontWeight={600}>
              Interne Datenablage
            </Typography>
          </Box>
        }
      >
        {getChildDirectories(props.documentDirectoryArray, null).map(
          documentDirectory => renderDirectory(documentDirectory)
        )}
      </TreeItem>
    </TreeView>
  );
};
